import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./GisData.css"; 
import { supabase } from "../supabaseClient"; 

function GisLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null); 
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {   
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        const { data: profile } = await supabase
          .from("users")
          .select("name, role")
          .eq("auth_id", session.user.id)
          .single();
        setUser({
          id: session.user.id,
          name: profile?.name || "User",
          role: profile?.role || "user",
        });
      }
      setLoading(false);
    });
    
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session?.user) {
        const { data: profile} = await supabase
          .from("users")
          .select("name, role")
          .eq("auth_id", session.user.id)
          .single();
        
        setUser({
          id: session.user.id,
          name: profile?.name || "User",
          role: profile?.role || "user",
        });
      } else {
        setUser(null);
        navigate("/", { replace: true }); // Added replace here for cleaner history tracking
      }
      setLoading(false);
    });
    return () => subscription.unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (!loading && !user) {
      navigate("/", { replace: true });
    }
  }, [user, loading, navigate]);
 
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const adminRoutes = ["/admin-dashboard", "/user-dashboard"];
    if (adminRoutes.includes(location.pathname)) {
      setOpenMenu("admin");
    }
  }, [location.pathname]);

  // 🛠️ FIX 1: Removed "!user" from blocking layout renders completely
  if (loading) return <div className="layout-loading">Loading...</div>;

  const handleLogout = async () => {
    try {  
      setShowDropdown(false);  
      localStorage.clear();
      sessionStorage.clear();   
      const { error } = await supabase.auth.signOut();    
      if (error) {
        console.error("Supabase signout returned an error:", error.message);
      }
    } catch (err) {
      console.error("Execution error during logout sequence:", err);
    }
  };

  const isActive = (path) => location.pathname === path;
  
  // 🛠️ FIX 2: Added optional chaining fallback logic so it never throws an error if user drops to null
  const userInitial = user?.name ? user.name.charAt(0).toUpperCase() : "U";
  const userRole = user?.role || "user";

  return (
    <div className="layout">
      {mobileMenuOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
      
      {/* 🔵 Sidebar */}
      <div className={`sidebar ${collapsed ? "collapsed" : ""} ${mobileMenuOpen ? "active" : ""}`}>
        <div className="toggle-btn" onClick={() => setCollapsed(!collapsed)}>
          ☰
        </div>

        <div
          className={`menu-item ${isActive("/gis-data") ? "active" : ""}`}
          onClick={() => {
            navigate("/gis-data");
            setMobileMenuOpen(false);
          }}
        >
          🏠 {!collapsed && "Dashboard"}
        </div>

        <div
          className={`menu-item ${isActive("/projects") ? "active" : ""}`}
          onClick={() => navigate("/projects")}
        >
          📁 {!collapsed && "Projects"}
        </div>

        <div
          className={`menu-item ${isActive("/tools") ? "active" : ""}`}
          onClick={() => navigate("/tool")}
        >
          📁 {!collapsed && "Tools"}
        </div>

        <div
          className={`menu-item ${isActive("/expense") ? "active" : ""}`}
          onClick={() => navigate("/expense")}
        >
          💰 {!collapsed && "Expenditure"}
        </div>

        {/* 🛠️ FIX 3: Safe access checking for user role */}
        {(userRole === "super_admin" || userRole === "superadmin") && (
          <>
            <div
              className="menu-item"
              onClick={() => setOpenMenu(openMenu === "admin" ? null : "admin")}
            >
              👨‍💼 {!collapsed && "Admin"} {!collapsed && (openMenu === "admin" ? "▾" : "▸")}
            </div>

            {openMenu === "admin" && !collapsed && (
              <div className="submenu">
                <div
                  className={`submenu-item ${isActive("/admin-dashboard") ? "active" : ""}`}
                  onClick={() => navigate("/admin-dashboard")}
                >
                  🛠 Admin Dashboard
                </div>
                <div
                  className={`submenu-item ${isActive("/user-dashboard") ? "active" : ""}`}
                  onClick={() => navigate("/user-dashboard")}
                >
                  👥 User Activity
                </div>
              </div>
            )}
          </>
        )}

        <div
          className={`menu-item ${isActive("/map") ? "active" : ""}`}
          onClick={() => navigate("/map")}
        >
          🗺️ {!collapsed && "Map"}
        </div>
      </div>

      {/* 🟢 RIGHT SIDE */}
      <div className="main">
        {/* 🔷 HEADER */}
        <div className="top-header">
          <button
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            ☰
          </button>

          <h3>Ramcharan Constructions</h3>

          {/* 👤 USER SECTION */}
          <div className="user-section" ref={dropdownRef}>
            <div
              className="user-pill"
              onClick={() => setShowDropdown(!showDropdown)}
            >
              <div className="user-avatar">{userInitial}</div>
              {/* 🛠️ FIX 4: Safe optional chaining on username text */}
              <span className="user-name-text">{user?.name || "Signing out..."}</span>
              <span className="arrow-down">{showDropdown ? "▴" : "▾"}</span>
            </div>

            {showDropdown && (
              <div className="user-dropdown-menu">
                <div className="dropdown-header">
                  <p className="user-role-badge">{userRole.replace("_", " ")}</p>
                </div>
                <div className="dropdown-divider"></div>
                <div
                  className="dropdown-item logout-btn"
                  onClick={handleLogout}
                >
                  🚪 Logout
                </div>
              </div>
            )}
          </div>
        </div>

        {/* CONTENT */}
        <div className="content">{children}</div>
      </div>
    </div>
  );
}

export default GisLayout;